import { useMemo } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import type { RouteObject } from "react-router-dom";
import { ProductsPage } from "./pages/ProductsPage";
import { ProductEditPage } from "./pages/ProductEditPage";
import {
  readFiniteIntParam,
  readSearchParam,
  type SearchParams,
} from "@/lib/routing/search-params";
import { useLocale } from "@seller/app/locale";
import { useSellerStoreSlug } from "@/lib/routing/useSellerStoreSlug";

function getSearchParamsRecord(searchParams: URLSearchParams): SearchParams {
  const entries = Array.from(searchParams.entries());

  return entries.reduce<SearchParams>((acc, [key, value]) => {
    const currentValue = acc[key];

    if (typeof currentValue === "undefined") {
      acc[key] = value;
      return acc;
    }

    if (Array.isArray(currentValue)) {
      acc[key] = [...currentValue, value];
      return acc;
    }

    acc[key] = [currentValue, value];
    return acc;
  }, {});
}

function ProductsRoute() {
  const locale = useLocale();
  const storeSlug = useSellerStoreSlug();
  const [searchParams] = useSearchParams();
  const resolvedSearchParams = useMemo(
    () => getSearchParamsRecord(searchParams),
    [searchParams],
  );
  const categoryIds = searchParams.getAll("categoryIds[]");
  const availability = readSearchParam(resolvedSearchParams, "availability") ?? "";
  const status = readSearchParam(resolvedSearchParams, "status") ?? "";
  const search = readSearchParam(resolvedSearchParams, "search") ?? "";
  const notice = readSearchParam(resolvedSearchParams, "notice") ?? "";
  const limit = readFiniteIntParam(resolvedSearchParams, "limit", {
    defaultValue: 20,
    min: 1,
    max: 100,
  });
  const offset = readFiniteIntParam(resolvedSearchParams, "offset", {
    defaultValue: 0,
    min: 0,
  });

  return (
    <ProductsPage
      locale={locale}
      storeSlug={storeSlug}
      categoryIds={categoryIds}
      availability={availability}
      status={status}
      search={search}
      notice={notice}
      limit={limit}
      offset={offset}
    />
  );
}

function ProductEditRoute() {
  const locale = useLocale();
  const storeSlug = useSellerStoreSlug();
  const { id = "" } = useParams();

  return (
    <ProductEditPage
      locale={locale}
      storeSlug={storeSlug}
      productId={id}
    />
  );
}

export const productRoutes: RouteObject[] = [
  {
    path: "products",
    element: <ProductsRoute />,
  },
  {
    path: "products/:id",
    element: <ProductEditRoute />,
  },
];
